import {
  Component,
  h,
  Prop,
  State,
  Listen,
  Event,
  EventEmitter,
  Method,
  Host,
  Element,
  Watch,
} from "@stencil/core";

@Component({
  tag: "signet-select",
  styleUrl: "signet-select.scss",
  shadow: false,
})

export class SignetSelect {
  /**
   * The `select` component is a custom dropdown menu that allows users to select one or more options from a list.
   * It can be configured with various properties such as size, label, default value, and whether it supports multiple selections.
   * The component also includes features like search functionality and event handling for option selection.
   */

  $selectContent: HTMLElement;
  /**
   * The `$selectContent` property is a reference to the select component's content area.
   * It is used to manage the visibility and behavior of the dropdown menu.
   */
  $contents: HTMLElement;

  /**
   * The `$contents` property is a reference to the contents of the select component.
   * It is used to manage the visibility and behavior of the dropdown menu.
   */
  $focusableEls: HTMLElement[];

  /**
   * The `$focusableEls` property is an array of references to the focusable elements within the select component.
   * It is used to manage keyboard navigation and selection of options.
   */
  $control: HTMLElement;

  /**
   * The `titleId` is a unique identifier for the select component, used for accessibility purposes.
   * It is generated dynamically to ensure uniqueness across multiple instances of the component.
   */
  
  titleId = `select_title_${selectIds++}`;
  
  /**
   * The `size` property determines the size of the select component.
   * It can be set to 'sm', 'md', 'lg', 'small', 'medium', or 'large' to adjust the appearance of the component accordingly.
   * The default size is 'md'.
   */

  @Prop() size: 'sm' | 'md' | 'lg' | 'small' | 'medium' | 'large' = 'md';

  private get normalizedSize(): 'sm' | 'md' | 'lg' {
    switch (this.size) {
      case 'small':
        return 'sm';
      case 'medium':
        return 'md';
      case 'large':
        return 'lg';
      case 'sm':
      case 'md':
      case 'lg':
      default:
        return this.size as 'sm' | 'md' | 'lg';
    }
  }

  /**
   * The `label` property is a string that represents the label for the select component.
   * It is displayed above the selected value and can be used to provide context to the user.
   */
  @Prop() label: string;

  /**
   * The `defaultValue` property is a string that represents the default value of the select component.
   * It is used to initialize the selected value when the component is first rendered.
   */
  @Prop({ reflect: true }) defaultValue: string;
  /**
   * The `modelValue` property enables two-way binding with v-model in Vue.
   */
  @Prop({ mutable: true, reflect: true }) modelValue?: string;

  // @Prop({ mutable: true, reflect: true }) modelvalue?: string;

  @Watch('modelValue')
  handleModelValueChange(newValue: string) {
    console.log("[SignetSelect]zz modelValue changed:", newValue);
    this.optionLabel = newValue;
    this.defaultValue = newValue;
    this.value = newValue;

    this.$el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: newValue,
    }));

    console.log(this.$el);

     this.$el.dispatchEvent(new CustomEvent('update:modelvalue', {
      bubbles: true,
      composed: true,
      detail: newValue,
    }));

    this.$el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: newValue,
    }));

  }

  @Watch('defaultValue')
  handleDefaultValueChange(newValue: string) {
    console.log("[SignetSelect] defaultValue changed:", newValue);
    this.selectedOption = newValue;
    this.optionLabel = newValue;
  }

  /**
   * The `value` property is added for Vue 2 v-model compatibility.
   */
  @Prop({ mutable: true, reflect: true }) value?: string;

  /**
   * The `value` property is a string that represents the currently selected value of the select component.
   * It is used to display the selected option and can be updated when an option is selected.
   */ 
  @Watch('value')
  handleValueChange(newVal: string) {
  if (this.modelValue !== newVal) {
    console.log("[SignetSelect] value changed:", newVal);
    this.modelValue = newVal;
    this.updateModelValue.emit(newVal);
    this.$el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: newVal,
    }));
    this.$el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: newVal,
    }));
  }
}

  /**
   * The `multiple` property is a boolean that determines whether the select component allows multiple selections.
   * If set to true, users can select more than one option from the list.
   */
  @Prop() multiple = false;

  /**
   * The `placeholder` property is a string that represents the placeholder text for the select component.
   * It is displayed when no option is selected and can be used to provide guidance to the user on what to select.
   */
  @Prop() placeholder: string;


  /**
   * The `disabled` property is a boolean that determines whether the select component is disabled.
   * If set to true, users cannot interact with the component or select any options.
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * The `readonly` property is a boolean that determines whether the select component is read-only.
   * If set to true, users cannot change the selected value, but can still interact with the component.
   */
  @Prop({ reflect: true }) readonly = false;

  /**
   * The `options` property is an array or JSON string representing the options available in the select component.
   * It can be an array of objects or a JSON string. Each object can contain properties such as `value`, `label`, and `disabled` to customize the appearance and behavior of each option.
   * This single prop replaces the previous `items` prop for all option data.
   */
  @Prop({ mutable: true }) options: string | any[];

  /**
   * The `items` property is an alias for the `options` property, allowing users to pass options as an array or JSON string.
   * It is maintained for backward compatibility but should be replaced with the `options` prop in new implementations.
   */
  @Prop() items: any;

  @Watch('items')
  syncLegacyItems() {
    if (!this.options && this.items) {
      console.warn('[SignetSelect] The "items" prop is deprecated. Please use "options" instead.');
      this.options = this.items;
    }
  }

  /**
   * The `hasSearch` property is a boolean that determines whether the select component includes a search functionality.
   * If set to true, users can search for options within the dropdown menu.
   */
  @Prop() hasSearch = false;

  /**
   * The `hasOnlySearch` property is a boolean that determines whether the select component only includes a search functionality.
   * If set to true, users can only search for options within the dropdown menu, without selecting any options.
   */
  @Prop() hasOnlySearch = false;


  /**
   * The `searchPlaceholder` property is a string that represents the placeholder text for the search input within the select component.
   * It is displayed when no search term is entered and can be used to provide guidance to the user on what to search for.
   */
  @State() selectedOption: string;

  /**
   * The `optionLabel` property is a string that represents the label of the currently selected option.
   * It is used to display the selected value in the select component.
   */ 
  @State() optionLabel: string;

  /**
   * The `isExpanded` property is a boolean that determines whether the dropdown menu of the select component is currently expanded.
   * If set to true, the dropdown menu is visible; if set to false, it is hidden.
   */
  @State() isExpanded = false;

  /**
   * The `focusIndex` property is a number that represents the index of the currently focused option within the dropdown menu.
   * It is used to manage keyboard navigation and selection of options.
   */
  @State() focusIndex = 0;

  @State() labelBgColor = 'transparent';

  /**
   * The `hidden` event is emitted when the select component is hidden.
   * It can be used to trigger actions or updates in other parts of the application when the dropdown menu is closed. 
   */
  @Event() hidden: EventEmitter;

  /**
   * The `shown` event is emitted when the select component is shown.
   * It can be used to trigger actions or updates in other parts of the application when the dropdown menu is opened.
   */
  @Event() shown: EventEmitter;

  /**
   * The `selectedValue` event is emitted when an option is selected from the dropdown menu.
   * It can be used to trigger actions or updates in other parts of the application based on the selected value.
   */
  @Event() selectedValue: EventEmitter<string>;

  /**
   * The `optionSelected` event is emitted when an option is selected from the dropdown menu.
   * It can be used to trigger actions or updates in other parts of the application based on the selected option.
   */

  @Element() $el: HTMLElement;
  /**
   * The `optionSelected` event is emitted when an option is selected from the dropdown menu.
   * It can be used to trigger actions or updates in other parts of the application based on the selected option.
   */
  @Event() optionSelected: EventEmitter<string>;

  /**
   * The `updateModelValue` event is emitted for v-model compatibility in Vue.
   */
  @Event() updateModelValue: EventEmitter<string>;
  
  /**
   * The `hide` method is used to hide the select component's dropdown menu.
   * It sets the `isExpanded` state to false and emits the `hidden` event.
   * This method can be called programmatically to close the dropdown menu.  
   * @returns {Promise<void>} A promise that resolves when the dropdown menu is hidden.
   */
  @Method()
  async hide() {
    this.isExpanded = false;
    this.focusIndex = 0;
    this.hidden.emit();
    // setTimeout(() => this.$control.focus());
  }

  /**
   * The `show` method is used to display the select component's dropdown menu.
   * It sets the `isExpanded` state to true and emits the `shown` event.
   * This method can be called programmatically to open the dropdown menu.  
   * @returns {Promise<void>} A promise that resolves when the dropdown menu is shown.
   */
  @Method()
  async show() {
    this.isExpanded = true;
    this.labelBgColor = this.computeEffectiveBackground(this.$el);
    this.shown.emit();

    if (this.$focusableEls?.length > 0) {
      // setTimeout(() => this.$focusableEls[0].focus(), 100);
    }
  }



  private computeEffectiveBackground(el: HTMLElement | null): string {
    while (el) {
      const bg = getComputedStyle(el).backgroundColor;
      //  console.log("This is the Label BG on the element itself", bg, (el));
      if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') {
        
        const match = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (match) {
          const [, r, g, b] = match;
          return `rgb(${r}, ${g}, ${b})`;
        }
        // console.log("This is the Label final BG", bg);
        return bg; // fallback if no match
      }
      el = el.parentElement;
    }
    return 'transparent';
  }

  

  // componentDidRender() {
  //   this.$focusableEls = Array.from(
  //     this.$contents.querySelectorAll(FOCUSABLE_ELEMENTS)
  //   );
  // }

  /**
   * The `componentWillRender` lifecycle method is called before the component is rendered.
   * It emits the `selectedValue` event with the default value of the select component.
   * This method can be used to perform any necessary setup or initialization before rendering the component.
   */
  componentWillRender() {
    if (this.defaultValue) {
      this.selectedValue.emit(this.defaultValue);
    }
  }

  /**
   * The `componentDidLoad` lifecycle method is called after the component has been loaded.
   * It adds an event listener to the window to handle clicks outside the select component,
   * preventing the dropdown menu from closing if the click is inside the component or on a prevent-close element.
   * It also registers a listener for option selection events.
   */

  
  componentWillLoad() {
    // Synchronize legacy `items` prop with `options`
    this.syncLegacyItems();

    // Prefer modelValue, then value, then defaultValue for initial selection
    if (this.modelValue) {
      this.selectedOption = this.modelValue;
      this.optionLabel = this.modelValue;
    } else if (this.value) {
      this.selectedOption = this.value;
      this.optionLabel = this.value;
    } else if (this.defaultValue) {
      this.selectedOption = this.defaultValue;
      this.optionLabel = this.defaultValue;
    }

    window.addEventListener("click", (e: MouseEvent) => {
      if (!this.isExpanded) return;

      const $preventCloseElements = Array.from(
        document.querySelectorAll(".prevent-menu-close")
      );
      const isPreventClose =
        this.$el.contains(e.target as HTMLElement) ||
        $preventCloseElements.some((x) => x.contains(e.target as HTMLElement));
      if (isPreventClose) return;
      this.hide();
    });
    this.labelBgColor = this.computeEffectiveBackground(this.$el);
    // console.log("This is the Label BG", this.labelBgColor);
  }


  private toggleSelect() {
    this.isExpanded = !this.isExpanded;
    this.isExpanded ? this.show() : this.hide();
  }

  private getOptions() {
  if (!this.options) return <slot />;

  const parsedOptions = Array.isArray(this.options)
    ? this.options
    : typeof this.options === 'string'
    ? (() => {
        try {
          return JSON.parse(this.options);
        } catch {
          return this.options
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean)
            .map((x) => ({ value: x, label: x }));
        }
      })()
    : [];

  return parsedOptions.map((option: any) => (
    <signet-option
      value={String(option.value ?? option)}
      label={String(option.label ?? option)}
    />
  ));
}

  @Listen("closeSelect")
  handleCloseSelect() {
    this.hide();
  }

  @Listen("optionLabel")
  handleOptionSelected(event: CustomEvent<string>) {
    const value = event.detail;

    this.defaultValue = value;
    this.selectedOption = value;
    this.optionLabel = value;
    this.modelValue = value;

    // Ensure the internal value prop reflects the selected option for Vue 2 v-model compatibility
    this.value = value;

    this.selectedValue.emit(value);
    this.optionSelected.emit(value);
    this.updateModelValue.emit(value);

    // Vue 3 v-model support
    this.$el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: value,
    }));

    // Vue 2 v-model support
    this.$el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: value,
    }));

    console.log("Emitting v-model update:", value);
  }

  /**
   * The `required` property determines if the select is required. If true, a colored asterisk will be shown in the label.
   */
  @Prop() required = false;

  /**
   * The `requiredAsteriskColor` property allows customizing the color of the required asterisk in the label.
   * Defaults to 'red' if not specified.
   */
  @Prop() requiredAsteriskColor = 'red';

  render() {
    const selectClasses = {
      "signet-select": true,
      "show-select-contents": this.isExpanded,
      [`signet-select--${this.normalizedSize}`]: true,
    };

    const contentClasses = {
      "select-contents-container": true,
      "select-contents": true,
    };

    return (
      <Host class={selectClasses}>
        <div
          class="select"
          onClick={this.disabled ? undefined : () => this.toggleSelect()}
          aria-disabled={this.disabled ? "true" : null}
          aria-expanded={this.isExpanded}
          id={this.titleId}
        >
          <span id={this.titleId} class="select-value">
              {this.optionLabel || this.modelValue || this.value || this.defaultValue}
          </span>
          {this.label ? (
            <span
            class={{
              'select-label-open': !!this.optionLabel || this.isExpanded,
              'select-label': !this.optionLabel && !this.isExpanded,
              'invisible': this.placeholder?.length > 0 && !this.isExpanded && !this.optionLabel,
            }}
          >
            {this.isExpanded || !!this.optionLabel || !!this.defaultValue  ?  (
              <span
                class="floating-label-bg"
                style={{ backgroundColor: this.labelBgColor }}
              />
            ) : (
              ""
            )}
            {this.label}
            {this.required && (
              <span style={{ color: this.requiredAsteriskColor, marginLeft: '2px' }}>*</span>
            )}
            </span>
          ) : (
            ""
          )}
          <div class="icons">
            {(this.hasSearch || this.hasOnlySearch) && (
              <signet-icon icon="search" class="search-icon"></signet-icon>
            )}
            {!this.hasOnlySearch && (
              <signet-icon
                icon={
                  !this.disabled ? "keyboard_arrow_down" : "arrow_drop_down"
                }
                class="select-icon select-icon"
              ></signet-icon>
            )}
          </div>
        </div>
        <div class={contentClasses} ref={(el) => (this.$selectContent = el)}>
          {this.getOptions()}
        </div>
      </Host>
    );
  }
}

let selectIds = 0;
